import React, { createContext, ReactNode, useContext, useState } from "react";

export type ToggleDrawerProps = {
  opened?: boolean;
  content?: React.ReactNode;
  headerText?: string;
  footer?: React.ReactNode;
};
export type DrawerContextType = {
  isDrawerOpen: boolean;
  drawerContent: ReactNode;
  drawerHeader: ReactNode;
  drawerFooter: ReactNode;
  toggleDrawer: (props: ToggleDrawerProps) => void;
  updateDrawerContent: (content: React.ReactNode, opened?: boolean) => void;
  updateDrawerFooter: (footer: React.ReactNode) => void;
  closeDrawer: () => void;
};

const DrawerContext = createContext<DrawerContextType | null>(null);

export const useDrawer = () => useContext(DrawerContext) as DrawerContextType;

export const DrawerProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState<ReactNode>(null);
  const [drawerHeader, setDrawerHeader] = useState<ReactNode>(null);
  const [drawerFooter, setDrawerFooter] = useState<ReactNode>(null);

  const toggleDrawer = (props: ToggleDrawerProps) => {
    const opened = props.opened;
    const content = props.content;
    setDrawerContent(content);
    setDrawerHeader(props.headerText);
    setDrawerFooter(props.footer);
    setDrawerOpen(typeof opened !== "undefined" ? opened : !isDrawerOpen);
  };

  const updateDrawerContent = (content: ReactNode, opened?: boolean) => {
    setDrawerContent(content);
    toggleDrawer({
      opened,
    });
  };

  const updateDrawerFooter = (footer: ReactNode) => {
    setDrawerFooter(footer);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  return (
    <DrawerContext.Provider
      value={{
        isDrawerOpen,
        toggleDrawer,
        closeDrawer,
        updateDrawerContent,
        updateDrawerFooter,
        drawerContent,
        drawerHeader,
        drawerFooter,
      }}
    >
      {children}
    </DrawerContext.Provider>
  );
};
